import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { decodeHTML } from "@utils/CommonUtils";
import { drilldownData, ModuleConfig } from "@utils/drilldownData";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import { useNotification } from "~/Hooks/useNotification";
import { eramanCMSApi } from "~/Services";
import { renderActionsColumn, renderDateColumn, renderImageColumn } from "~/Utils/TableRenderFunction";
import { useRestrictedItemsSectionsList } from "../../Pages.hook";

export const RestrictedItemsSectionsList = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const fourthChildActiveTab = query.get("fourthChildActiveTab");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.nid);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useRestrictedItemsSectionsList(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    if (searchValue?.trim()) {
      const filteredData = data.filter((item: any) => {
        const title = item.english_title ? item.english_title.toLowerCase() : "";
        const search = decodeHTML(searchValue).trim().toLowerCase();

        return decodeHTML(title).includes(search);
      });
      setSortedData(filteredData);
    } else {
      setSortedData(data);
    }
  }, [data, searchValue]);

  const getRecords = () => {
    setIsLoading(true);
    const imageSection =
      fourthChildActiveTab === "restrictedItemsCheckedInCabinRestrictionsImage" ? "checkedin" : "cabinonly";
    let url = `/api/restricted/item/section-two?image_section=${imageSection}`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        } else {
          setData([]);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 100,
      render: renderImageColumn,
    },
    {
      title: "Title",
      dataIndex: "english_title",
      key: "english_title",
      width: 350,
      render: (value: any) => decodeHTML(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "english_title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "english_title"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Published",
    //   dataIndex: "published",
    //   key: "published",
    //   width: 100,
    //   render: renderPublishedColumnStatus,
    //   sorter: true,
    //   sortOrder:
    //     sortOrder === "asc" && orderByName === "date"
    //       ? "ascend"
    //       : sortOrder === "desc" && orderByName === "date"
    //         ? "descend"
    //         : undefined,
    // },
    // {
    //   title: "Created At",
    //   dataIndex: "createdAt",
    //   key: "createdAt",
    //   fixed: "right",
    //   render: (value: any) => renderDateColumn(value),
    //   sorter: true,
    //   sortOrder:
    //     sortOrder === "asc" && orderByName === "createdAt"
    //       ? "ascend"
    //       : sortOrder === "desc" && orderByName === "createdAt"
    //         ? "descend"
    //         : undefined,
    // },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      fixed: "right",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(
              `/dedicated/eraman/pages/travellersInfo/restrictedItems/add?type=${fourthChildActiveTab}&editId=${record?.nid}`
            );
          },
          undefined,
          setDeleteModalVisible
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.nid);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else if (sortOrder === undefined) {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...data];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={0}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  english_title: decodeHTML(d.english_title),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Title"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          // total={totalRecords}
          total={sortedData.length}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={
          drilldownData[_get(drilldown, "subModule", "restrictedItemsCheckedInCabinRestrictionsImage") as ModuleConfig]
        }
        data={drilldown?.[0]}
      />
    </>
  );
};
function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (sortBy === "title") {
    const titleA = a[sortBy]?.toLowerCase() || "";
    const titleB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") {
      return titleA < titleB ? 1 : -1;
    } else if (order === "asc") {
      return titleA > titleB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}
function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
